import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { X, Check, ExternalLink, ShieldCheck } from "lucide-react";

function canvaEmbed(url) {
    let base = (url || "").split("?")[0].replace(/\/$/, "");
    if (!/\/(view|watch)$/.test(base)) base += "/view";
    return `${base}?embed`;
}

export default function MeetingViewer({ meeting, onClose, onAcked }) {
    const [watched, setWatched] = useState(meeting.watched_by_me);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    // A <video> element can't send an Authorization header, and a browser
    // blocking third-party cookies won't attach the session cookie to a media
    // request either. So the URL carries its own credential -- a ticket scoped
    // to this one video that dies in two minutes, never the session token.
    const [videoSrc, setVideoSrc] = useState(null);
    const [videoErr, setVideoErr] = useState(null);

    // The check-off used to be tappable the instant the viewer opened, which
    // meant a compliance record could say "watched" for a meeting someone
    // dismissed in two seconds. The report built on those records is the whole
    // point of this screen, so the button now waits for evidence.
    //
    // Video is measurable: watch to VIDEO_COMPLETE_FRACTION and it unlocks.
    // A Canva deck is a cross-origin iframe that reports nothing back, so the
    // only honest signal is time spent with it open -- a floor, not proof.
    //
    // Deliberately forgiving: a video that fails to load unlocks immediately.
    // Blocking a check-off because of bad signal on a job site would just move
    // the problem to the foreman chasing people who genuinely did watch.
    const VIDEO_COMPLETE_FRACTION = 0.9;
    const CANVA_MIN_SECONDS = 30;
    const [progressMet, setProgressMet] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(
        meeting.source_type === "canva" ? CANVA_MIN_SECONDS : null
    );

    useEffect(() => {
        if (meeting.source_type !== "canva" || watched) return;
        const t = setInterval(() => {
            setSecondsLeft((s) => {
                if (s === null || s <= 1) { clearInterval(t); setProgressMet(true); return 0; }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(t);
    }, [meeting.source_type, watched]);

    const onTimeUpdate = (e) => {
        const el = e.currentTarget;
        if (!el.duration || progressMet) return;
        if (el.currentTime / el.duration >= VIDEO_COMPLETE_FRACTION) setProgressMet(true);
    };

    // Already checked off, or nothing measurable to wait for.
    const canAck = watched || progressMet || !!videoErr;

    useEffect(() => {
        if (meeting.source_type !== "video") return;
        let cancelled = false;
        api.post(`/safety/video/${meeting.meeting_id}/ticket`)
            .then((r) => {
                if (cancelled) return;
                setVideoSrc(`${API}/safety/video/${meeting.meeting_id}?ticket=${encodeURIComponent(r.data.ticket)}`);
            })
            .catch(() => {
                if (cancelled) return;
                setVideoErr("Couldn’t start the video. Check your signal and reopen it.");
            });
        return () => { cancelled = true; };
    }, [meeting.meeting_id, meeting.source_type]);

    const ack = async () => {
        setSaving(true);
        setErr(null);
        try {
            await api.post(`/safety/meetings/${meeting.meeting_id}/ack`);
            setWatched(true);
            onAcked && onAcked(meeting.meeting_id);
        } catch (e) {
            setErr(e?.response?.data?.detail || "Could not record. Try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/80 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="meeting-viewer">
            <div className="bg-white w-full sm:max-w-3xl sm:rounded-2xl shadow-lift max-h-[100vh] sm:max-h-[92vh] flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-brand-charcoal text-brand-bone shrink-0">
                    <div className="font-bold truncate pr-2" data-testid="viewer-title">{meeting.title}</div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/15 shrink-0" data-testid="viewer-close"><X className="h-5 w-5" /></button>
                </div>

                <div className="flex-1 overflow-auto bg-black">
                    {meeting.source_type === "video" ? (
                        videoErr ? (
                            <div className="p-8 text-center text-brand-bone/80 text-sm" data-testid="viewer-video-error">{videoErr}</div>
                        ) : videoSrc ? (
                            <video
                                src={videoSrc}
                                controls
                                playsInline
                                preload="metadata"
                                onTimeUpdate={onTimeUpdate}
                                onEnded={() => setProgressMet(true)}
                                className="w-full h-full max-h-[70vh] bg-black"
                                data-testid="viewer-video"
                            />
                        ) : (
                            <div className="p-8 text-center text-brand-bone/60 text-sm" data-testid="viewer-video-loading">Loading video…</div>
                        )
                    ) : (
                        <div className="relative w-full" style={{ aspectRatio: "16 / 9" }}>
                            <iframe
                                src={canvaEmbed(meeting.canva_url)}
                                title={meeting.title}
                                allow="fullscreen; autoplay"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full"
                                data-testid="viewer-canva"
                            />
                        </div>
                    )}
                </div>

                <div className="px-4 py-4 bg-white border-t border-brand-charcoal/10 shrink-0 space-y-3">
                    {meeting.source_type === "canva" && (
                        <a href={meeting.canva_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-navy" data-testid="viewer-open-canva">
                            <ExternalLink className="h-4 w-4" /> Open full screen (with sound) in a new tab
                        </a>
                    )}
                    {err && <div className="text-sm text-brand-danger" data-testid="viewer-error">{err}</div>}
                    {watched ? (
                        <div className="w-full h-14 rounded-xl bg-brand-success text-white font-bold flex items-center justify-center gap-2" data-testid="viewer-watched-state">
                            <ShieldCheck className="h-5 w-5" /> You're checked off — thanks!
                        </div>
                    ) : (
                        <button onClick={ack} disabled={saving || !canAck} className="w-full h-14 rounded-xl bg-brand-navy hover:bg-brand-navy-dark text-brand-bone font-bold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="viewer-ack-button">
                            <Check className="h-5 w-5" /> {saving ? "Saving…" : "I watched this"}
                        </button>
                    )}
                    <p className="text-xs text-brand-charcoal/50 text-center" data-testid="viewer-ack-hint">
                        {watched
                            ? "Your foreman can see you're covered."
                            : canAck
                                ? "Check off so your foreman knows you're covered."
                                : meeting.source_type === "canva"
                                    ? `Have a read through — you can check off in ${secondsLeft}s.`
                                    : "Watch the whole thing — the check-off unlocks near the end."}
                    </p>
                </div>
            </div>
        </div>
    );
}
